import {
  Button,
  Divider,
  Dropdown,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  EToastVariant,
  Heading,
  Loader,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Texture from '@/assets/become-a-host/rv/texture.svg';
import { useIncome } from '@/components/list-your-rv/PathCard/useIncome';
import Icon from '@/components/switchback/Icon/IconComponent';
import { vehicleTypes } from '@/constants/vehicleDetails';
import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { setListingVehicleType } from '@/redux/modules/listingVehicleType';
import { TRootState } from '@/redux/rootReducer';
import { getAuthenticatedUser } from '@/redux/selectors/auth/user';
import { trackEarningsEstimateCompleted } from '@/services/analytics/lyrv';
import { getOwnerReportCard } from '@/services/getOwnerDetail';
import { TVehicleType } from '@/services/types/search/rentals/id';
import { formatCurrency, getCurrencyCode } from '@/utility/currency';
import { getOwnerListings } from '@/utility/ownerListings';

import styles from '../BecomeAHost.module.css';
import { useHostingRVFlow } from './useHostingRVFlow';

export const StartListingForm = () => {
  const intl = useIntl();
  const { showToast } = useToast();
  const userData = useSelector(getAuthenticatedUser);
  const vehicleTypeDropdownLabel = intl.formatMessage({
    defaultMessage: 'Vehicle type',
    id: 'COwWYM',
  });
  const selectedVehicleType = useSelector((state: TRootState) => state.listingVehicleType?.data);
  const dispatch = useDispatch();
  const { redirectToHostingRVFlow, loading } = useHostingRVFlow();
  const {
    data: income,
    isValidating: isValidatingIncome,
    error: incomeError,
  } = useIncome(selectedVehicleType || undefined);
  const earnings = !income ? 0 : (income?.high.day || 0) * 365;
  const route = useRouter();
  const currency = getCurrencyCode(route.locale);

  const handleSelectedVehicleType = (type: string) => {
    dispatch(setListingVehicleType(type as TVehicleType));
  };

  useEffect(() => {
    const handleEarningsEstimateCompletedEvent = async (userId: number, isOwner: boolean) => {
      if (!selectedVehicleType) return;
      const reportCard = await getOwnerReportCard(userId);

      const { publishedListings, unpublishedListings } = getOwnerListings(
        reportCard?.data?.rental_report,
        isOwner,
      );

      trackEarningsEstimateCompleted({
        isOwner,
        totalPublishedListings: publishedListings,
        totalUnpublishedListings: unpublishedListings,
        vehicleType: selectedVehicleType,
        estimatedEarningsPerYear: earnings,
        rentalType: 'rv',
      });
    };
    if (earnings && userData?.id) {
      handleEarningsEstimateCompletedEvent(userData.id, !!userData?.owner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earnings, userData?.id]);

  const vehicleTypeOptions = Object.keys(vehicleTypes).map(type => {
    const typedVehicleType = type as TVehicleType;
    return (
      <Dropdown.Option
        key={typedVehicleType}
        value={typedVehicleType}
        content={{ label: intl.formatMessage(vehicleTypes[typedVehicleType].label) }}
        icon={<Icon name={vehicleTypes[typedVehicleType].icon} />}
      />
    );
  });

  useEffect(() => {
    if (incomeError) {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({ defaultMessage: 'Earnings Calculation Error', id: 'j9Nbbm' }),
        intl.formatMessage({
          defaultMessage:
            'Unable to calculate estimated earnings at the moment. Please try again later.',
          id: 'US1I5K',
        }),
      );
    }
  }, [incomeError, intl, showToast]);

  return (
    <section
      className={`${styles.spacing} relative flex flex-col items-center text-white bg-bark-30`}>
      <Texture className="absolute top-0 left-0 w-full h-full" />
      <div className="z-10 flex flex-col items-center text-center">
        <Text variant={ETextStyleVariant.LegalRegular} className="uppercase">
          <FormattedMessage defaultMessage="host more, earn more" id="GYKrc3" />
        </Text>

        <Heading variant={EHeadingStyleVariant.H3} className="mt-2 mb-12">
          <FormattedMessage defaultMessage="How much could you make?" id="ti03k/" />
        </Heading>

        <div className="flex flex-col items-center mb-12 md:flex-row gap-4">
          <Heading variant={EHeadingStyleVariant.H4Alt}>
            <FormattedMessage defaultMessage="Share your" id="VQJ5Bz" />
          </Heading>

          <Dropdown
            className="w-52 [&>button]:!rounded-full"
            ariaLabel={vehicleTypeDropdownLabel}
            label={vehicleTypeDropdownLabel}
            onChange={handleSelectedVehicleType}
            multiple={false}
            inputValue={selectedVehicleType || ''}>
            {vehicleTypeOptions}
          </Dropdown>
        </div>

        <div className="flex items-center mb-8">
          {isValidatingIncome ? (
            <Loader />
          ) : (
            <>
              {!earnings ? (
                <Heading variant={EHeadingStyleVariant.H2}>$ --,---&nbsp;</Heading>
              ) : (
                <Heading variant={EHeadingStyleVariant.H2}>
                  {formatCurrency({
                    currency: currency || 'USD',
                    priceInCents: earnings * 100,
                  })}
                  &nbsp;
                </Heading>
              )}
              <Heading variant={EHeadingStyleVariant.H5}>
                <FormattedMessage defaultMessage="/ year" id="8hNOud" />
              </Heading>
            </>
          )}
        </div>

        <Divider className="mx-auto border-b-4 border-b-canvas-100 max-w-25" />
        <Button
          onDark
          onClick={redirectToHostingRVFlow}
          variant={EButtonColorVariant.Secondary}
          label={intl.formatMessage({ defaultMessage: 'Try hosting today', id: 'R7Xwt5' })}
          className="mt-8"
          loading={loading}
        />
      </div>
    </section>
  );
};
